<template>
  <vue-html2pdf
      :key="payment.id"
      :show-layout="false"
      :float-layout="true"
      :enable-download="false"
      :preview-modal="true"
      :paginate-elements-by-height="1350"
      :filename="invoiceFileName"
      :pdf-quality="2"
      :manual-pagination="false"
      pdf-format="a4"
      pdf-orientation="portrait"
      pdf-content-width="800px"
      :html-to-pdf-options="{
          margin: [4, 0, 40, 0],
          filename: invoiceFileName,
          image: {
              type: 'jpeg',
              quality: 2
          },
          pagebreak: { mode: ['css', 'legacy'], avoid: 'img' },
          enableLinks: false,
          html2canvas: {
              scale: 1,
              useCORS: true,
          },
          jsPDF: {
              format: 'a4',
              orientation: 'portrait'
          }
      }"
      ref="html2Pdf"
      @beforeDownload="beforeDownload($event)"
  >
    <section slot="pdf-content">
      <section class="pdf-item">
        <v-container fluid style="font-family: Arial, sans-serif; font-size: 14px;">
          <v-row justify="space-between">
            <v-col cols="12">
              <h3 style="text-align: center;" class="accent--text">SELF-BILLING INVOICE</h3>
            </v-col>
            <v-col cols="7">
              <v-simple-table dense>
                <tbody>
                <tr>
                  <td>
                    <div style="margin-bottom: 20px;">
                      <div>{{ payment.affiliate_id }}</div>
                      <div>{{ affiliateDisplayName }}</div>
                      <div v-for="(line, idx) in affiliateAddressLines" :key="`aff-addr-${idx}`">
                        {{ line }}
                      </div>
                      <div v-if="affiliateInfo.vat_number">{{ affiliateInfo.vat_number }}</div>
                      <div v-if="payment.affiliate && payment.affiliate.email">
                        {{ payment.affiliate.email }}
                      </div>
                    </div>
                    <div>
                      <v-row justify="start">
                        <v-col sm="auto">
                          <div>
                            Invoiced to:
                            <h4>
                              <p>Ad Network Solutions BV</p>
                              <p>Sarphatistraat 656-658</p>
                              <p>1018 AV Amsterdam</p>
                              <p>The Netherlands</p>
                              <p>VAT number: NL864584726B01</p>
                            </h4>
                          </div>
                        </v-col>
                      </v-row>
                    </div>
                  </td>
                </tr>
                </tbody>
              </v-simple-table>
            </v-col>
            <v-col cols="5">
              <v-simple-table dense>
                <tbody>
                <tr>
                  <td class="text-right">Date:</td>
                  <td>{{getInvoiceDate()}}</td>
                </tr>
                <tr>
                  <td class="text-right">Invoice #:</td>
                  <td>{{getInvoiceName()}}</td>
                </tr>
                <tr>
                  <td class="text-right">Period Start:</td>
                  <td>{{getDate(payment.start_period)}}</td>
                </tr>
                <tr>
                  <td class="text-right">Period End:</td>
                  <td>{{getDate(payment.end_period)}}</td>
                </tr>
                <tr>
                  <td class="text-right">Payment Term:</td>
                  <td>{{payment.term_desc || 'NET-15'}}</td>
                </tr>
                <tr>
                  <td class="text-right">Due Date:</td>
                  <td>{{getDate(payment.end_period, payment.term_value || 15)}}</td>
                </tr>
                <tr>
                  <td class="text-right">Currency:</td>
                  <td>{{payment.currencyName}}</td>
                </tr>
                </tbody>
              </v-simple-table>
            </v-col>
          </v-row>
          <v-row justify="space-between">
            <v-col cols="12">
              <v-simple-table>
                <thead>
                <tr>
                  <th style="text-align: left;">
                    #
                  </th>
                  <th style="text-align: left;">
                    Item Name
                  </th>
                  <th style="text-align: left;">
                    Qty
                  </th>
                  <th style="text-align: left;">
                    Price
                  </th>
                  <th style="text-align: left;">
                    Rate
                  </th>
                  <th style="text-align: left;">
                    Amount
                  </th>
                </tr>
                </thead>
                <tbody>
                  <tr v-for="(item,index) in paymentItems" :key="`payItem-${item.id}`">
                    <td>{{index + 1}}</td>
                    <td>{{item.itemName}}</td>
                    <td>{{item.count}}</td>
                    <td>{{item.itemCurrency}}{{item.amount}}</td>
                    <td>{{item.currencyRate}}</td>
                    <td>{{payment.currencySymbol}}{{
                        item.sumAmount.toFixed(2)
                      }}</td>
                  </tr>
                  <tr v-if="payment.referral_commission > 0">
                    <td>{{paymentItems.length + 1}}</td>
                    <td>Commission</td>
                    <td>1</td>
                    <td>{{payment.currencySymbol}}{{Number(payment.referral_commission).toFixed(2)}}</td>
                    <td>1</td>
                    <td>{{payment.currencySymbol}}{{Number(payment.referral_commission).toFixed(2)}}</td>
                  </tr>
                  <tr v-if="payment.carry_over_referral_commission > 0">
                    <td>{{paymentItems.length + (payment.referral_commission > 0 ? 2 : 1)}}</td>
                    <td>Carried Over Commission</td>
                    <td>1</td>
                    <td>{{payment.currencySymbol}}{{Number(payment.carry_over_referral_commission).toFixed(2)}}</td>
                    <td>1</td>
                    <td>{{payment.currencySymbol}}{{Number(payment.carry_over_referral_commission).toFixed(2)}}</td>
                  </tr>
                  <tr>
                    <td colspan="5" style="text-align: right;">Subtotal</td>
                    <td>{{payment.currencySymbol}}{{invoiceTotal.toFixed(2)}}</td>
                  </tr>
                  <tr>
                    <td colspan="5" style="text-align: right;"><strong>Total:</strong></td>
                    <td><strong>{{payment.currencySymbol}}{{invoiceTotal.toFixed(2)}}</strong></td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-col>
          </v-row>
          <v-row justify="space-between" class="payment-instructions-block">
            <v-col cols="8">
              <v-simple-table dense>
                <tbody>
                <tr>
                  <td style="line-height: initial; font-size: 10px;">
                    <h3 style="font-size: 13px;">
                      Payment Instructions
                    </h3>
                    <span style="margin-bottom: 10px;">Payment Method:
                        <strong>{{payment.paymentType}}</strong>
                      </span>
                    <div v-for="item in visiblePaymentDetailFields"
                         :key="`pf-${item.field}`">
                      <h4
                        style="margin-top: 5px; font-size: 11px;"
                        v-if="item.title"
                        :key="`pf-title_${item.field}`"
                      >
                        {{item.title}}:
                      </h4>
                      <div v-if="item.hasValue">
                        <span>{{item.label}}</span>:
                        <strong>{{paymentMethodData[item.field]}}</strong>
                      </div>
                    </div>
                  </td>
                </tr>
                </tbody>
              </v-simple-table>
            </v-col>
          </v-row>
        </v-container>
      </section>
    </section>
  </vue-html2pdf>
</template>

<script>
import { DateTime } from 'luxon';
import VueHtml2pdf from 'vue-html2pdf';
import PaymentDetails from '@/utils/form.PaymentDetails';

export default {
  name: 'PdfContent',
  props: {
    payment: {
      type: Object,
    },
    paymentHistory: {
      type: Object
    },
    paymentItems: {
      type: Array,
    },
    paymentDetails: {
      type: Array,
    },
    fileName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      payment_details_fields: PaymentDetails,
    };
  },
  mounted() {
  },
  methods: {
    async getPaymentItems() {
      console.log('payment items requested');
    },
    async downloadInvoice() {
      console.log(`this is called outside`);
      console.log(`this it payment ${this.payment.id}`);
      console.log(this.payment.id);
      // get payments items, set them to data property, then call generate pdf function
      this.$refs.html2Pdf.generatePdf();
    },
    getInvoiceName() {
      const endPeriod = DateTime.fromISO(this.payment.end_period, { zone: 'UTC' }).toFormat('yyyyLLdd');
      return `${this.payment.affiliate_id}-${endPeriod}-${this.payment.id}`;
    },
    getInvoiceDate() {
      // Calculate invoice date: use date_added from payment history if available, otherwise use end_period
      let invoiceDate;
      if (this.paymentHistory && this.paymentHistory.date_added) {
        invoiceDate = DateTime.fromISO(this.paymentHistory.date_added, { zone: 'UTC' });
      } else {
        invoiceDate = DateTime.fromISO(this.payment.end_period, { zone: 'UTC' });
      }
      
      // Calculate due date: end_period + term_value
      const dueDate = DateTime.fromISO(this.payment.end_period, { zone: 'UTC' })
        .plus({ days: this.payment.term_value || 15 });
      
      // If invoice date is greater than due date, cap it at due date
      if (invoiceDate > dueDate) {
        return dueDate.toFormat('DDD');
      }
      
      return invoiceDate.toFormat('DDD');
    },
    getDate(date, add) {
      const today = DateTime.fromISO(date, { zone: 'UTC' });
      if (add) {
        return today.plus({ days: add }).toFormat('DDD');
      }
      return today.toFormat('DDD');
    },
    async beforeDownload({ html2pdf, options, pdfContent }) {
      console.log(`this is called before download`);
      console.log(`this it payment ${this.payment.id}`);
      console.log(html2pdf, options);
      console.log('[HISTORY]', this.paymentHistory);
      const newOptions = { ...options };
      newOptions.filename = this.invoiceFileName;
      // vue-html2pdf height pagination often inserts a blank leading page
      await html2pdf().set(newOptions).from(pdfContent).toPdf()
        .get('pdf')
        .then((pdf) => {
          if (pdf.internal.getNumberOfPages() > 1) {
            pdf.deletePage(1);
          }
        })
        .save();
    },
  },
  computed: {
    affiliateInfo() {
      if (this.payment?.affiliate?.affiliate_information &&
          this.payment.affiliate.affiliate_information.length > 0) {
        return this.payment.affiliate.affiliate_information[0];
      }
      return {};
    },
    affiliateAddress() {
      const info = this.affiliateInfo;
      return {
        street_address: info.street_address || null,
        street_address_2: info.street_address_2 || null,
        city: info.city || null,
        state: info.state || null,
        postal_code: info.postal_code || null,
        country: info.country || null,
      };
    },
    paymentMethodData() {
      const paymentMethodData = {};
      if (this.paymentDetails && this.paymentDetails.length) {
        this.paymentDetails.forEach((i) => {
          paymentMethodData[i.field] = i.value;
        });
      }
      return paymentMethodData;
    },
    visiblePaymentDetailFields() {
      const fields = this.payment_details_fields[this.payment.paymentTypeId] || [];
      const hasValue = (field) => {
        const value = this.paymentMethodData[field];
        return value !== undefined && value !== null && String(value).trim() !== '';
      };

      // Group by section title; keep title only if section has at least one filled field
      const sections = [];
      let current = { titleItem: null, items: [] };

      fields.forEach((item) => {
        if (item.title) {
          if (current.titleItem || current.items.length) {
            sections.push(current);
          }
          current = { titleItem: item, items: [item] };
          return;
        }
        current.items.push(item);
      });
      if (current.titleItem || current.items.length) {
        sections.push(current);
      }

      const visible = [];
      sections.forEach((section) => {
        const filled = section.items.filter((item) => hasValue(item.field));
        if (!filled.length) {
          return;
        }
        filled.forEach((item, index) => {
          visible.push({
            ...item,
            // Keep section title only on the first filled field of the section
            title: index === 0 ? (section.titleItem && section.titleItem.title) : null,
            hasValue: true,
          });
        });
      });

      return visible;
    },
    affiliateDisplayName() {
      return this.affiliateInfo.company_name
        || this.paymentMethodData.beneficiary_name
        || this.paymentMethodData.payoneer_username
        || this.payment.affiliateName
        || '';
    },
    affiliateAddressLines() {
      const lines = [];
      const address = this.affiliateAddress;

      if (address.street_address) {
        lines.push(address.street_address);
      }
      if (address.street_address_2) {
        lines.push(address.street_address_2);
      }

      const cityLine = [
        address.city,
        address.state,
        address.postal_code,
      ].filter(Boolean).join(', ');
      if (cityLine) {
        lines.push(cityLine);
      }
      if (address.country) {
        lines.push(address.country);
      }

      if (lines.length) {
        return lines;
      }

      if (this.affiliateInfo.company_address) {
        return [this.affiliateInfo.company_address];
      }

      if (this.paymentMethodData.beneficiary_address_1) {
        const beneficiaryLines = [];
        const address1 = [
          this.paymentMethodData.beneficiary_address_1,
          this.paymentMethodData.beneficiary_address_2,
        ].filter(Boolean).join(' ');
        if (address1) {
          beneficiaryLines.push(address1);
        }
        if (this.paymentMethodData.beneficiary_zip) {
          beneficiaryLines.push(`(${this.paymentMethodData.beneficiary_zip})`);
        }
        if (this.paymentMethodData.beneficiary_city) {
          beneficiaryLines.push(this.paymentMethodData.beneficiary_city);
        }
        if (this.paymentMethodData.beneficiary_country) {
          beneficiaryLines.push(this.paymentMethodData.beneficiary_country);
        }
        return beneficiaryLines;
      }

      return [];
    },
    invoiceFileName() {
      return `invoice_period_${
          DateTime.fromISO(this.payment.start_period, { zone: 'UTC' }).toISODate()
      }_${
          DateTime.fromISO(this.payment.end_period, { zone: 'UTC' }).toISODate()
      }_${Math.round(DateTime.utc().toSeconds())}.pdf`;
    },
    invoiceTotal() {
      const commission = Number(this.payment?.referral_commission ?? 0);
      const carryOverCommission = Number(this.payment?.carry_over_referral_commission ?? 0);
      const commissionTotal = (Number.isFinite(commission) ? commission : 0)
        + (Number.isFinite(carryOverCommission) ? carryOverCommission : 0);

      if (Array.isArray(this.paymentItems) && this.paymentItems.length) {
        return this.paymentItems.reduce((sum, item) => {
          const lineAmount = Number(item.sumAmount ?? 0);
          return sum + (Number.isFinite(lineAmount) ? lineAmount : 0);
        }, 0) + commissionTotal;
      }

      // Fallback for edge cases where invoice lines are not loaded.
      const paymentTotal = Number(this.payment?.total);
      if (Number.isFinite(paymentTotal)) {
        return paymentTotal;
      }

      const paymentAmount = Number(this.payment?.amount);
      if (Number.isFinite(paymentAmount)) {
        return paymentAmount;
      }

      return 0;
    },
  },
  components: {
    VueHtml2pdf,
  },
};
</script>

<style scoped>
</style>
